import { useEffect, useState } from "react";

import { AlertBar2 } from "@/ui2/atoms/AlertBar";
import { ModalControlled2 } from "@/ui2/molecules/Modal";
import { useGetPresignedUrl } from "@/features/s3/api/queries/useGetPresignedUrl";
import type { UUID } from "@/core/types";

import { IdentityDocumentPreviewImage } from "./IdentityDocumentPreviewImage";
import { IdentityDocumentPreviewLoader } from "./IdentityDocumentPreviewLoader";

type IdentityDocumentPreviewModalProps = {
  entityId: UUID;
  isModalOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export function IdentityDocumentPreviewModal({
  entityId,
  isModalOpen,
  onOpenChange,
}: IdentityDocumentPreviewModalProps) {
  const { data, error, isLoading } = useGetPresignedUrl({
    options: {
      /** Fetch presigned url only when modal is open */
      enabled: isModalOpen,
    },
    payload: {
      entity_id: entityId,
      entity_type: "patient_personal_id_image",
      file_sub_type: "PERSONAL_ID_FRONT",
      file_type: "PATIENT_PERSONAL_ID_IMAGE",
    },
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  /** Reset image load state when modal opens
   * Useful when user has cache disabled and reopens the modal
   */
  useEffect(() => {
    if (!isModalOpen) return;

    setIsImageLoaded(false);
  }, [isModalOpen]);

  return (
    <ModalControlled2 isOpen={isModalOpen} onOpenChange={onOpenChange} title="View ID">
      <div className="tw:flex tw:aspect-3/2 tw:h-82.5 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-stroke-quaternary tw:p-3">
        {error && (
          <AlertBar2 variant="error">
            <b>Preview Error: </b>An error occurred while fetching the document. Please try again.
          </AlertBar2>
        )}

        {isLoading || !isImageLoaded ? <IdentityDocumentPreviewLoader /> : null}

        {data && (
          <IdentityDocumentPreviewImage
            isImageLoaded={isImageLoaded}
            onImageLoad={() => setIsImageLoaded(true)}
            src={data.message}
          />
        )}
      </div>
    </ModalControlled2>
  );
}
